// Adapter between the new tales `/v1` API and the flat tale shape the skazki
// components already consume. The new API returns a nested envelope
// `{tale: {phase, status, params, assets, playback, ...}}`; this module flattens
// it so SkazkiCard / SkazkiNightPlayer / MyContentPage didn't have to change
// shape when the backend moved off `/celebs/skazki`.

// Collapse the new derived status enum (draft|queued|generating|completed|
// partial|failed|canceled) onto the flat statuses the UI branches on.
export function mapStatus(status, hasPlayback) {
    switch (status) {
        case 'completed':
            return 'ready';
        case 'partial':
            // A partial tale is still playable as long as audio landed.
            return hasPlayback ? 'ready' : 'failed';
        case 'queued':
        case 'generating':
            return 'processing';
        case 'failed':
        case 'canceled':
            return 'failed';
        case 'draft':
            return 'draft';
        default:
            return 'processing';
    }
}

function scenesText(scenes) {
    if (!Array.isArray(scenes) || !scenes.length) return '';
    return scenes.map(s => s.text).filter(Boolean).join('\n\n');
}

// `assets.text.raw_value` is a JSON document `{version, title, scenes:[...]}` —
// NOT a plain string. Parse it as a fallback for when playback isn't ready yet
// (text can be done before audio).
function textDocScenes(textAsset) {
    if (!textAsset?.raw_value) return [];
    try {
        const doc = JSON.parse(textAsset.raw_value);
        return Array.isArray(doc?.scenes) ? doc.scenes : [];
    } catch {
        return [];
    }
}

function titleFor(title, params) {
    if (title) return title;
    if (params?.child_name) return `Сказка для ${params.child_name}`;
    return 'Сказка';
}

// Accepts either the full snapshot `{tale}` or a bare tale object.
export function normalizeTale(snapshot) {
    if (!snapshot) return null;
    const tale = snapshot.tale || snapshot;
    if (!tale || !tale.id) return null;

    const params = tale.params || {};
    const playback = tale.playback || null;
    const playbackScenes = playback?.scenes || [];
    const scenes = playbackScenes.length ? playbackScenes : textDocScenes(tale.assets?.text);
    // Scene timing (start/end) is only present on playback scenes; the text-doc
    // fallback carries text alone, so timings stay null until audio lands.
    const chapters = scenes.map((s, i) => ({
        index: s.index ?? i,
        text: s.text,
        start: s.start ?? null,
        end: s.end ?? null,
    }));

    return {
        id: tale.id,
        status: mapStatus(tale.status, !!playback),
        phase: tale.phase || null,
        // A preview tale (committed without a credit) is locked until /unlock.
        paid: !tale.is_preview,
        title: titleFor(tale.title, params),
        // titleFor always synthesizes something; the player needs to know
        // whether the real (LLM-written) title has landed yet.
        has_title: !!tale.title,
        text: scenesText(scenes),
        chapters: chapters.length ? chapters : null,
        audio_url: playback?.audio_url || null,
        // Word-level timings for the read-along player (alignment.json).
        alignment_url: playback?.alignment_url || null,
        cover_url: tale.assets?.cover?.url || null,
        duration_sec: playback?.duration_sec || null,
        child_name: params.child_name || null,
        age: params.age ?? null,
        interests: params.interests || [],
        voice_id: params.voice_id || null,
        created_at: tale.created_at || null,
        progress: tale.progress || null,
        // Kept for a future SSE upgrade; unused while polling.
        last_event_id: tale.last_event_id ?? null,
    };
}

// `GET /v1/tales` list items carry only {id,type,phase,status,title,cover_url,
// params,created_at} — no playback/assets, so no audio_url/duration here.
export function normalizeListItem(item) {
    if (!item) return null;
    const params = item.params || {};
    return {
        id: item.id,
        title: titleFor(item.title, params),
        status: mapStatus(item.status, true),
        paid: !item.is_preview,
        child_name: params.child_name || null,
        age: params.age ?? null,
        cover_url: item.cover_url || null,
        created_at: item.created_at || null,
    };
}
